import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Workflow, Plus, Loader2, ArrowLeft, Bot, MessagesSquare, Calendar,
  HelpCircle, Save, Trash2, Power, PowerOff, Copy, ExternalLink,
  BookOpen, Code2, MessageCircle, Eye, User as UserIcon, Check,
  Sparkles, Globe, ShieldCheck, Settings, Headphones, UserCheck, PenLine, Users2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { api, errorMessage, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { ModuleGuide } from "@/components/ModuleGuide";
import { AGENTS_GUIDE } from "@/lib/guides";
import { SamTipCard } from "@/components/SamTipCard";
import { useSamTip } from "@/context/SamTipsContext";
import { TemplatePreviewPane } from "@/components/TemplatePreviewPane";

const AGENT_TYPES = [
  { id: "website_chatbot",   name: "Website Chatbot",   icon: Globe,          desc: "Answers questions for visitors on your site." },
  { id: "lead_qualifier",    name: "Lead Qualifier",    icon: MessagesSquare, desc: "Captures structured lead info and creates a lead." },
  { id: "faq_bot",           name: "FAQ / Support Bot", icon: HelpCircle,     desc: "Strict-RAG bot grounded in your KB." },
  { id: "appointment_booker", name: "Appointment Booker", icon: Calendar,    desc: "Books meetings, captures name/email/time." },
];

const STATUS_PILL = {
  active:   "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  inactive: "border-white/15 bg-white/5 text-white/55",
};

function typeLabel(t) { return (AGENT_TYPES.find((x) => x.id === t) || {}).name || t; }
function typeIcon(t) { return (AGENT_TYPES.find((x) => x.id === t) || {}).icon || Bot; }

// ============ LIST PAGE ============

export default function Agents() {
  const navigate = useNavigate();
  const tip = useSamTip("agents");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/agents");
      setAgents(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/agents/${delTarget.id}`);
      toast.success("Agent deleted");
      setDelTarget(null);
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="mx-auto max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-eyebrow text-amber-400" data-testid="agents-eyebrow">AI Agents</div>
            <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
              Autonomous helpers that <span className="brand-text">never sleep.</span>
            </h1>
            <p className="mt-2 max-w-xl text-white/55">
              Build, embed, and ship agents that answer questions, qualify leads, and book
              meetings — grounded in your own knowledge base.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="agents-new-btn">
            <Plus className="mr-1.5 h-4 w-4" /> New agent
          </Button>
        </div>
      </motion.div>

      <div className="mt-8">
        <SamTipCard
          show={!loading && agents.length === 0}
          enabled={tip.enabled}
          message={tip.message}
          prefill={tip.prefill}
          testId="sam-tip-agents"
          module="agents"
        />
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/45"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : agents.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center" data-testid="agents-empty">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl brand-bg">
              <Workflow className="h-6 w-6 text-white" />
            </div>
            <h2 className="font-display mt-5 text-2xl font-light">No agents yet</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-white/55">
              Build your first agent in under 60 seconds. Pick a type, write a persona, and ship it.
            </p>
            <Button onClick={() => setCreateOpen(true)} className="mt-6 rounded-full bg-white text-black hover:bg-zinc-200" data-testid="agents-empty-create">
              <Plus className="mr-1.5 h-4 w-4" /> Create your first agent
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="agents-grid">
            {agents.map((a, i) => (
              <AgentCard key={a.id} agent={a} index={i}
                onOpen={() => navigate(`/app/agents/${a.id}`)}
                onDelete={() => setDelTarget(a)} />
            ))}
          </div>
        )}
      </div>

      <CreateAgentDialog open={createOpen} onOpenChange={setCreateOpen}
        onCreated={(a) => { setCreateOpen(false); navigate(`/app/agents/${a.id}`); }} />

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{delTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">
              This permanently deletes the agent, all conversations, and any captured bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 text-white hover:bg-red-600" data-testid="agent-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AgentCard({ agent, index, onOpen, onDelete }) {
  const Icon = typeIcon(agent.type);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: index * 0.04 }}
      className="glass glass-hover group relative cursor-pointer overflow-hidden rounded-2xl p-5"
      onClick={onOpen}
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl"
            style={{ background: `linear-gradient(135deg, ${agent.accent_color}33, ${agent.accent_color}11)`, border: `1px solid ${agent.accent_color}30` }}
          >
            <Icon className="h-5 w-5 text-white/95" />
          </div>
          <div className="min-w-0">
            <div className="font-display truncate text-lg font-light text-white" title={agent.name}>{agent.name}</div>
            <div className="mt-0.5 text-xs text-white/45">{typeLabel(agent.type)}</div>
          </div>
        </div>
        <span className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider ${STATUS_PILL[agent.status]}`}>
          {agent.status}
        </span>
      </div>
      <div className="mt-3 line-clamp-2 text-xs text-white/45">{agent.description || "—"}</div>
      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-white/5 pt-3 text-center">
        <Stat label="Convos" value={agent.stats?.conversations || 0} />
        <Stat label="Leads" value={agent.stats?.leads_captured || 0} />
        <Stat label="Bookings" value={agent.stats?.bookings || 0} />
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute right-3 top-3 rounded-lg p-1.5 text-white/30 opacity-0 transition hover:bg-red-500/10 hover:text-red-300 group-hover:opacity-100"
        data-testid={`agent-delete-${agent.id}`}
        title="Delete"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-eyebrow text-white/40">{label}</div>
      <div className="font-display mt-1 text-xl text-white">{value}</div>
    </div>
  );
}

// Iter 22 (audit D1) — Template gallery. Picking a template pre-fills
// the entire agent config (type + style preset + system prompt seed +
// suggested prompts) so non-technical owners never start from blank.
const AGENT_TEMPLATES = [
  {
    id: "support",
    name: "Customer Support Bot",
    description: "Answers product questions, escalates to humans when stuck.",
    icon: Headphones,
    type: "website_chatbot",
    style_preset: "professional",
    system_prompt: "You are a friendly customer support assistant. Answer questions about the product accurately using the knowledge base. If you don't know the answer, say so politely and offer to connect the visitor with a human via email.",
    welcome_message: "Hi! I can answer product questions or help you reach our team. What's up?",
    suggested_prompts: [
      "How do I get started?",
      "What's included in the price?",
      "Can I talk to a human?",
    ],
  },
  {
    id: "qualifier",
    name: "Sales Qualifier",
    description: "Qualifies leads, captures intent, schedules demos.",
    icon: UserCheck,
    type: "lead_qualifier",
    style_preset: "professional",
    system_prompt: "You qualify inbound leads. Ask short, polite questions to learn the visitor's company, team size, primary use-case, and timeline. End by offering to schedule a 20-min demo.",
    welcome_message: "Hey! Got a couple of quick questions to see if we're the right fit.",
    suggested_prompts: [
      "Book a demo",
      "Tell me about pricing",
      "Is this right for my team?",
    ],
  },
  {
    id: "writer",
    name: "Content Writer",
    description: "Drafts emails, social posts, blog ideas in your tone.",
    icon: PenLine,
    type: "faq_bot",
    style_preset: "friendly",
    system_prompt: "You draft marketing + sales content (cold emails, LinkedIn posts, blog hooks). Always sound human + specific to the company's context. Use the knowledge base for facts. Never invent statistics.",
    welcome_message: "Hi! What would you like me to write today?",
    suggested_prompts: [
      "Write a cold email to a marketing manager",
      "Draft a LinkedIn post about our launch",
      "Suggest 3 blog post angles",
    ],
  },
  {
    id: "internal",
    name: "Internal Helper",
    description: "Answers your team's questions from your private docs.",
    icon: Users2,
    type: "faq_bot",
    style_preset: "concise",
    system_prompt: "You answer questions from internal team members using the company knowledge base. Be terse and direct. Cite the source doc when you do. If unsure, say so — never guess.",
    welcome_message: "Internal Q&A — fire away.",
    suggested_prompts: [
      "What's our refund policy?",
      "Where do I find Q4 priorities?",
      "Summarize the latest product changes",
    ],
  },
];

function CreateAgentDialog({ open, onOpenChange, onCreated }) {
  // Iter 25 (D1) — Added "preview" step. Each template card has a Preview
  // button that drops the user into a live demo of the template's
  // behaviour before they commit. After watching, they can either
  // "Use this template" (→ details) or "All templates" (→ template).
  const [step, setStep] = useState("template"); // "template" | "preview" | "details"
  const [template, setTemplate] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [type, setType] = useState("website_chatbot");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) {
      setStep("template"); setTemplate(null); setPreviewing(null);
      setType("website_chatbot"); setName(""); setDescription("");
    }
  }, [open]);
  const pickTemplate = (t) => {
    setTemplate(t);
    setType(t.type);
    setName(t.name);
    setStep("details");
  };
  const openPreview = (t) => {
    setPreviewing(t);
    setStep("preview");
  };
  const closePreview = () => {
    setPreviewing(null);
    setStep("template");
  };
  const skipTemplate = () => {
    setTemplate(null);
    setStep("details");
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type, name: name.trim(), description: description.trim() || null,
      };
      if (template) {
        payload.system_prompt = template.system_prompt;
        payload.welcome_message = template.welcome_message;
        payload.suggested_prompts = template.suggested_prompts;
        payload.style_preset = template.style_preset;
      }
      const r = await api.post("/agents", payload);
      toast.success("Agent created");
      onCreated?.(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-2xl" data-testid="create-agent-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {step === "template" ? "Pick a starting point" :
             step === "preview" ? "Watch it work" :
             "Name your agent"}
          </DialogTitle>
          <DialogDescription className="text-white/55">
            {step === "template"
              ? "Pick a template to skip the blank page — you can edit everything later."
              : step === "preview"
              ? "A live sample conversation — exactly what this template feels like out of the box."
              : "We'll seed the rest. You can tweak persona + tone once it's created."}
          </DialogDescription>
        </DialogHeader>

        {step === "template" ? (
          <div className="space-y-3" data-testid="template-gallery">
            <div className="grid gap-2 sm:grid-cols-2">
              {AGENT_TEMPLATES.map((t) => {
                const Icon = t.icon;
                return (
                  // Iter 25 (D1) — Each card now exposes TWO actions: a
                  // Preview button (drops into a live demo of the
                  // template's behaviour) and a "Use" button (the
                  // existing pick path). We swapped the outer <button>
                  // for a <div> so the preview link doesn't nest
                  // interactive elements.
                  <div
                    key={t.id}
                    className="group flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3 transition hover:border-fuchsia-400/40 hover:bg-fuchsia-500/[0.06]"
                    data-testid={`template-${t.id}`}
                  >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-fuchsia-500/15 text-fuchsia-300">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-white">{t.name}</div>
                      <div className="mt-0.5 text-[11px] text-white/50">{t.description}</div>
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openPreview(t)}
                          className="rounded-full border border-white/15 px-2.5 py-0.5 text-[11px] text-white/75 transition hover:border-fuchsia-400/40 hover:bg-fuchsia-500/10 hover:text-fuchsia-100"
                          data-testid={`template-preview-${t.id}`}
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => pickTemplate(t)}
                          className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-medium text-black transition hover:bg-zinc-200"
                          data-testid={`template-use-${t.id}`}
                        >
                          Use this template
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={skipTemplate}
                className="text-xs text-white/45 hover:text-white"
                data-testid="template-skip"
              >
                Start from blank →
              </button>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/65 hover:text-white">Cancel</Button>
            </div>
          </div>
        ) : step === "preview" && previewing ? (
          // Iter 25 (D1) — live template preview with streamed sample turns.
          <TemplatePreviewPane
            template={previewing}
            onBack={closePreview}
            onUseTemplate={(t) => { closePreview(); pickTemplate(t); }}
          />
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {template ? (
              <div className="rounded-xl border border-fuchsia-500/25 bg-fuchsia-500/[0.06] p-3 text-xs text-fuchsia-200">
                Using template <span className="font-medium text-white">{template.name}</span>.
                <button type="button" onClick={() => setStep("template")} className="ml-2 underline" data-testid="template-change">
                  Change
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-white/50">Type</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="agent-type-grid">
                  {AGENT_TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = type === t.id;
                    return (
                      <button key={t.id} type="button" onClick={() => setType(t.id)}
                        className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${active ? "border-fuchsia-500/40 bg-fuchsia-500/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`}
                        data-testid={`agent-type-${t.id}`}
                      >
                        <Icon className={`h-4 w-4 flex-shrink-0 ${active ? "text-fuchsia-300" : "text-white/70"}`} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white">{t.name}</div>
                          <div className="mt-0.5 text-[11px] text-white/45">{t.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/50">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Site Concierge" className="border-white/10 bg-black/40 text-white" data-testid="new-agent-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/50">Description (optional)</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="border-white/10 bg-black/40 text-white" data-testid="new-agent-desc" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/65 hover:text-white">Cancel</Button>
              <Button type="submit" disabled={saving || !name.trim()} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="new-agent-save">
                {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Create
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ BUILDER PAGE ============

export function AgentBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("setup");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/agents/${id}`);
      setAgent(r.data);
    } catch (err) {
      toast.error(errorMessage(err));
      navigate("/app/agents");
    } finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const patch = async (updates) => {
    try {
      const r = await api.patch(`/agents/${id}`, updates);
      setAgent(r.data);
      return r.data;
    } catch (err) { toast.error(errorMessage(err)); throw err; }
  };

  const toggleStatus = async () => {
    try {
      const r = await api.post(`/agents/${id}/toggle-status`);
      setAgent(r.data);
      toast.success(r.data.status === "active" ? "Agent activated" : "Agent deactivated");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-white/45"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  if (!agent) return null;
  const Icon = typeIcon(agent.type);

  return (
    <div className="mx-auto max-w-6xl">
      <Link to="/app/agents" className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white" data-testid="agent-back">
        <ArrowLeft className="h-3.5 w-3.5" /> All agents
      </Link>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl"
            style={{ background: `linear-gradient(135deg, ${agent.accent_color}40, ${agent.accent_color}10)`, border: `1px solid ${agent.accent_color}40` }}
          >
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-eyebrow text-white/40">{typeLabel(agent.type)}</div>
            <h1 className="font-display truncate text-3xl font-light text-white">{agent.name}</h1>
          </div>
          <span className={`hidden flex-shrink-0 sm:inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider ${STATUS_PILL[agent.status]}`}>
            {agent.status}
          </span>
        </div>
        <Button onClick={toggleStatus} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="agent-toggle-status">
          {agent.status === "active" ? <><PowerOff className="mr-1.5 h-4 w-4" /> Deactivate</> : <><Power className="mr-1.5 h-4 w-4" /> Activate</>}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white/5 text-white" data-testid="agent-tabs">
          <TabsTrigger value="setup"         className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="agent-tab-setup">Setup</TabsTrigger>
          <TabsTrigger value="knowledge"     className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="agent-tab-knowledge">Knowledge</TabsTrigger>
          <TabsTrigger value="embed"         className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="agent-tab-embed">Embed</TabsTrigger>
          <TabsTrigger value="conversations" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="agent-tab-conversations">Conversations</TabsTrigger>
          <TabsTrigger value="guide" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="agent-tab-guide">
            <HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="setup" className="mt-5">
          <SetupTab agent={agent} patch={patch} />
        </TabsContent>
        <TabsContent value="knowledge" className="mt-5">
          <KnowledgeTab agent={agent} patch={patch} />
        </TabsContent>
        <TabsContent value="embed" className="mt-5">
          <EmbedTab agent={agent} />
        </TabsContent>
        <TabsContent value="conversations" className="mt-5">
          <ConversationsTab agent={agent} />
        </TabsContent>
        <TabsContent value="guide" className="mt-5">
          <ModuleGuide
            title={AGENTS_GUIDE.title}
            whatItIs={AGENTS_GUIDE.whatItIs}
            capabilities={AGENTS_GUIDE.capabilities}
            howTo={AGENTS_GUIDE.howTo}
            ctaLabel={AGENTS_GUIDE.cta}
            onCta={() => setTab("setup")}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---- SETUP TAB ----

function SetupTab({ agent, patch }) {
  const [draft, setDraft] = useState({
    name: agent.name,
    description: agent.description || "",
    system_prompt: agent.system_prompt,
    welcome_message: agent.welcome_message,
    suggested_prompts: agent.suggested_prompts || [],
    model_id: agent.model_id,
    temperature: agent.temperature,
    style_preset: agent.style_preset || "professional",
    accent_color: agent.accent_color,
  });
  const [saving, setSaving] = useState(false);
  const [promptInput, setPromptInput] = useState("");
  const [models, setModels] = useState([]);
  // Iter 22 (audit D1) — Advanced settings closed by default. Non-technical
  // owners never see raw temperature/system prompt unless they ask for them.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  useEffect(() => {
    api.get("/chat/models")
      .then((r) => {
        const data = r.data;
        const list = Array.isArray(data?.models) ? data.models : (Array.isArray(data) ? data : []);
        setModels(list);
      })
      .catch(() => setModels([]));
  }, []);

  // Iter 22 — preset → temperature mapping kept here (and on the backend in
  // models_phase4.STYLE_PRESET_TEMPERATURE) so both surfaces agree.
  const PRESETS = [
    { id: "friendly",     label: "Friendly",     description: "Warm + conversational. First-person, light contractions, asks follow-ups.", temperature: 0.85 },
    { id: "professional", label: "Professional", description: "Clear business tone. Leads with the answer. No filler.",                       temperature: 0.60 },
    { id: "concise",      label: "Concise",      description: "Terse + direct. Answers in ≤2 sentences unless asked for more.",               temperature: 0.40 },
  ];

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setPreset = (id) => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setDraft((d) => ({ ...d, style_preset: id, temperature: p.temperature }));
  };
  const addPrompt = () => {
    const v = promptInput.trim();
    if (!v) return;
    if (draft.suggested_prompts.includes(v)) return;
    set("suggested_prompts", [...draft.suggested_prompts, v]);
    setPromptInput("");
  };
  const removePrompt = (p) => set("suggested_prompts", draft.suggested_prompts.filter((x) => x !== p));

  const save = async () => {
    setSaving(true);
    try { await patch(draft); toast.success("Saved"); }
    catch (_) {} finally { setSaving(false); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="setup-tab">
      <div className="glass space-y-4 rounded-2xl p-5">
        <Section label="Identity">
          <Field label="Name">
            <Input value={draft.name} onChange={(e) => set("name", e.target.value)} className="border-white/10 bg-black/40 text-white" data-testid="setup-name" />
          </Field>
          <Field label="Description">
            <Input value={draft.description} onChange={(e) => set("description", e.target.value)} placeholder="Short description for your team" className="border-white/10 bg-black/40 text-white" data-testid="setup-description" />
          </Field>
        </Section>

        {/* Iter 22 (audit D1) — plain-language Style preset replaces the
            raw temperature slider as the primary tone control. Maps under
            the hood to (temperature, system_prompt_prefix) on the backend. */}
        <Section label="Style">
          <div className="grid gap-2" data-testid="setup-style-presets">
            {PRESETS.map((p) => {
              const active = draft.style_preset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPreset(p.id)}
                  className={`group flex items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
                    active
                      ? "border-fuchsia-400/45 bg-fuchsia-500/10 ring-1 ring-fuchsia-400/30"
                      : "border-white/8 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.05]"
                  }`}
                  data-testid={`setup-style-preset-${p.id}`}
                >
                  <div className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${active ? "border-fuchsia-300 bg-fuchsia-300" : "border-white/30"}`}>
                    {active && <div className="h-1.5 w-1.5 rounded-full bg-black" />}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm ${active ? "text-white" : "text-white/80"}`}>{p.label}</div>
                    <div className="mt-0.5 text-xs text-white/45">{p.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </Section>

        <Section label="Greeting">
          <Field label="Welcome message">
            <Input value={draft.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} className="border-white/10 bg-black/40 text-white" data-testid="setup-welcome" />
          </Field>
        </Section>

        <Section label="Suggested prompts (up to 8)">
          <div className="flex gap-2">
            <Input
              value={promptInput} onChange={(e) => setPromptInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPrompt(); } }}
              placeholder="Type and press Enter"
              className="border-white/10 bg-black/40 text-white"
              data-testid="setup-prompt-input"
            />
            <Button type="button" variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10" onClick={addPrompt}>Add</Button>
          </div>
          {draft.suggested_prompts.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="setup-prompts">
              {draft.suggested_prompts.map((p) => (
                <button key={p} onClick={() => removePrompt(p)} className="rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-2 py-0.5 text-[11px] text-fuchsia-200 hover:bg-fuchsia-500/20">
                  {p} <span className="text-fuchsia-300/60">×</span>
                </button>
              ))}
            </div>
          )}
        </Section>

        <Section label="Brand">
          <Field label="Accent color">
            <div className="flex items-center gap-2">
              <input type="color" value={draft.accent_color} onChange={(e) => set("accent_color", e.target.value)}
                className="h-9 w-12 rounded border border-white/10 bg-transparent" data-testid="setup-accent" />
              <Input value={draft.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="border-white/10 bg-black/40 font-mono text-white" />
            </div>
          </Field>
        </Section>

        {/* Iter 22 (audit D1) — Advanced settings collapsed by default.
            Raw system prompt, temperature slider, model picker live here. */}
        <div className="rounded-xl border border-white/8 bg-white/[0.02]">
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm text-white/65 transition hover:text-white"
            data-testid="setup-advanced-toggle"
            aria-expanded={advancedOpen}
          >
            <span className="flex items-center gap-2"><Settings className="h-3.5 w-3.5" /> Advanced settings</span>
            <span className="text-xs text-white/40">{advancedOpen ? "Hide" : "Show"}</span>
          </button>
          {advancedOpen && (
            <div className="space-y-3 border-t border-white/5 px-3 py-3" data-testid="setup-advanced-panel">
              <Field label="System prompt">
                <Textarea value={draft.system_prompt} onChange={(e) => set("system_prompt", e.target.value)} rows={8}
                  placeholder="Who is this agent? How should it behave?"
                  className="border-white/10 bg-black/40 text-white font-mono text-sm"
                  data-testid="setup-system-prompt"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="LLM">
                  <Select value={draft.model_id} onValueChange={(v) => set("model_id", v)}>
                    <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="setup-model"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                      {models.length === 0 && <SelectItem value={draft.model_id}>{draft.model_id}</SelectItem>}
                      {models.map((m) => <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={`Temperature · ${draft.temperature.toFixed(2)}`}>
                  <Slider min={0} max={1.5} step={0.05} value={[draft.temperature]} onValueChange={(v) => set("temperature", v[0])} data-testid="setup-temp" />
                </Field>
              </div>
              <p className="text-[11px] text-white/35">
                The Style preset above sets sensible defaults. Power users can fine-tune temperature + the raw system prompt here.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="setup-save">
            {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            <Save className="mr-1.5 h-4 w-4" /> Save changes
          </Button>
        </div>
      </div>

      {/* Live preview */}
      <div className="glass space-y-3 rounded-2xl p-5">
        <div className="text-eyebrow text-white/40">Live preview</div>
        <ChatPreview agent={{ ...agent, ...draft }} />
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="space-y-2">
      <div className="text-eyebrow text-white/40">{label}</div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-white/50">{label}</Label>
      {children}
    </div>
  );
}

function ChatPreview({ agent }) {
  return (
    <div
      className="overflow-hidden rounded-2xl border"
      style={{ borderColor: `${agent.accent_color}40`, background: `linear-gradient(180deg, ${agent.bg_color || "#0a0a0a"}, #0a0a0a)` }}
    >
      <div className="flex items-center gap-2 border-b border-white/5 p-3" style={{ background: `${agent.accent_color}15` }}>
        <div className="flex h-7 w-7 items-center justify-center rounded-full" style={{ background: agent.accent_color }}>
          <Bot className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="text-sm text-white">{agent.name}</div>
      </div>
      <div className="space-y-2 p-3">
        <div className="rounded-2xl rounded-tl-md border border-white/5 bg-white/[0.03] p-3 text-sm text-white/90">
          {agent.welcome_message || "Hi! How can I help?"}
        </div>
        {(agent.suggested_prompts || []).slice(0, 3).map((p) => (
          <div key={p} className="rounded-full border border-white/10 bg-white/[0.02] px-3 py-1 text-xs text-white/65">{p}</div>
        ))}
      </div>
    </div>
  );
}

// ---- KNOWLEDGE TAB ----

function KnowledgeTab({ agent, patch }) {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(agent.kb_doc_ids || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/kb/documents").then((r) => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSelected((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const save = async () => {
    setSaving(true);
    try { await patch({ kb_doc_ids: selected }); toast.success(`${selected.length} doc${selected.length !== 1 ? "s" : ""} attached`); }
    catch (_) {} finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="knowledge-tab">
      <div className="glass rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-light">Attach knowledge</h3>
            <p className="mt-1 text-sm text-white/55">Selected docs are searched (semantic RAG) on every visitor message.</p>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="knowledge-save">
            {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Save
          </Button>
        </div>
        {loading ? (
          <div className="mt-6 flex items-center justify-center py-8 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : docs.length === 0 ? (
          <div className="mt-6 text-center text-sm text-white/55">
            No KB documents yet. <Link to="/app/knowledge" className="text-fuchsia-300 hover:underline">Upload one →</Link>
          </div>
        ) : (
          <div className="mt-5 space-y-2" data-testid="kb-docs">
            {docs.map((d) => {
              const checked = selected.includes(d.id);
              return (
                <button
                  key={d.id} type="button" onClick={() => toggle(d.id)}
                  className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition ${checked ? "border-fuchsia-500/30 bg-fuchsia-500/5" : "border-white/5 bg-white/[0.02] hover:bg-white/[0.05]"}`}
                  data-testid={`kb-doc-${d.id}`}
                >
                  <div className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${checked ? "border-fuchsia-400 bg-fuchsia-500" : "border-white/30"}`}>
                    {checked && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-white">{d.filename}</div>
                    <div className="text-xs text-white/45">{d.chunk_count} chunks · status: {d.status}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- EMBED TAB ----

function EmbedTab({ agent }) {
  const widgetUrl = `${window.location.origin}/widget/${agent.id}`;
  const backendOrigin = (API_BASE || "").replace(/\/api\/?$/, "");
  const snippet =
    `<script src="${window.location.origin}/widget-loader.js" data-agent-id="${agent.id}" data-backend="${backendOrigin}" async></script>`;

  const copy = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success("Copied"));

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="embed-tab">
      <div className="space-y-4">
        <div className="glass rounded-2xl p-5">
          <h3 className="font-display flex items-center gap-2 text-xl font-light"><Code2 className="h-4 w-4 text-fuchsia-300" /> Embed snippet</h3>
          <p className="mt-1 text-sm text-white/55">Paste this script tag just before <code className="rounded bg-white/10 px-1 text-fuchsia-200">{"</body>"}</code> on any HTML page.</p>
          <pre className="mt-4 overflow-x-auto rounded-xl border border-white/5 bg-black/60 p-3 text-[12px] text-fuchsia-200" data-testid="embed-snippet">{snippet}</pre>
          <Button onClick={() => copy(snippet)} variant="outline" className="mt-3 border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="embed-copy-snippet">
            <Copy className="mr-1.5 h-4 w-4" /> Copy snippet
          </Button>
        </div>

        <div className="glass rounded-2xl p-5">
          <h3 className="font-display flex items-center gap-2 text-xl font-light"><Globe className="h-4 w-4 text-fuchsia-300" /> Public share URL</h3>
          <p className="mt-1 text-sm text-white/55">A standalone, auth-free chat link you can share or embed in an iframe.</p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 truncate rounded-xl border border-white/5 bg-black/60 p-2 text-xs text-white/85" data-testid="widget-url">{widgetUrl}</code>
            <Button onClick={() => copy(widgetUrl)} variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="widget-copy-url">
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <a href={widgetUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="widget-open-url">
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </a>
          </div>
        </div>

        <div className="glass rounded-2xl p-5 text-xs text-white/55">
          <h3 className="font-display flex items-center gap-2 text-sm font-medium text-white"><ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Security</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>The public widget endpoint never exposes your system prompt, KB content, or org details.</li>
            <li>Each visitor gets a fresh conversation id; rate-limited per IP.</li>
            <li>If you deactivate the agent, the embed instantly stops responding.</li>
          </ul>
        </div>
      </div>

      <div className="glass overflow-hidden rounded-2xl p-3">
        <div className="text-eyebrow px-2 pb-2 text-white/40">Live preview · iframe</div>
        <iframe
          src={widgetUrl}
          title="Agent widget preview"
          className="h-[560px] w-full rounded-xl border border-white/5 bg-black"
          data-testid="embed-iframe-preview"
        />
      </div>
    </div>
  );
}

// ---- CONVERSATIONS TAB ----

function ConversationsTab({ agent }) {
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/agents/${agent.id}/conversations`);
      setConvos(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, [agent.id]);
  useEffect(() => { load(); }, [load]);

  const open = async (c) => {
    setOpenId(c.id); setDetail(null);
    try {
      const r = await api.get(`/agents/${agent.id}/conversations/${c.id}`);
      setDetail(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  if (loading) return <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  if (convos.length === 0) {
    return (
      <div className="glass rounded-2xl p-12 text-center text-white/55" data-testid="convos-empty">
        <MessageCircle className="mx-auto h-7 w-7 text-white/30" />
        <div className="font-display mt-3 text-xl font-light">No conversations yet</div>
        <p className="mx-auto mt-2 max-w-md text-sm">As soon as someone chats with your embedded widget, transcripts will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="convos-list">
      {convos.map((c) => (
        <div key={c.id} onClick={() => open(c)} className="glass glass-hover flex cursor-pointer items-center justify-between rounded-xl p-3" data-testid={`convo-row-${c.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm text-white">
              <UserIcon className="h-3.5 w-3.5 text-white/40" /> {c.visitor_id}
              {c.captured_lead_id && <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">Lead captured</span>}
              {c.booking_id && <span className="rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-1.5 py-0.5 text-[10px] text-fuchsia-300">Booking</span>}
            </div>
            <div className="mt-0.5 truncate text-xs text-white/45">
              {c.source_url || "—"} · {c.message_count} messages
            </div>
          </div>
          <div className="text-right text-xs text-white/45">
            <div className={`uppercase tracking-wider ${c.status === "open" ? "text-emerald-300" : "text-white/45"}`}>{c.status}</div>
            <div>{new Date(c.last_message_at).toLocaleString()}</div>
          </div>
        </div>
      ))}

      <Sheet open={!!openId} onOpenChange={(o) => !o && (setOpenId(null), setDetail(null))}>
        <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto border-white/10 bg-[#0a0a0a] text-white" data-testid="convo-drawer">
          {detail ? (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl text-white">Visitor · {detail.conversation.visitor_id}</SheetTitle>
                <SheetDescription className="text-white/55">
                  {detail.conversation.source_url || "—"} · {detail.messages.length} messages · started {new Date(detail.conversation.started_at).toLocaleString()}
                </SheetDescription>
              </SheetHeader>
              {Object.keys(detail.conversation.captured_data || {}).length > 0 && (
                <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-white/85" data-testid="convo-captured">
                  <div className="text-eyebrow text-emerald-300">Captured</div>
                  <div className="mt-1 space-y-1">
                    {Object.entries(detail.conversation.captured_data).map(([k, v]) => (
                      v ? <div key={k}><span className="text-white/45">{k}:</span> <span className="text-white">{String(v)}</span></div> : null
                    ))}
                  </div>
                  {detail.conversation.captured_lead_id && (
                    <div className="mt-2 text-emerald-300">
                      Saved as lead — <Link to="/app/leads" className="underline">view in Smart Leads →</Link>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 space-y-2">
                {detail.messages.map((m) => (
                  <div key={m.id} className={`rounded-2xl border p-3 text-sm ${m.role === "user" ? "border-white/5 bg-white/[0.02] text-white/90" : "border-fuchsia-500/15 bg-fuchsia-500/5 text-white/90"}`}>
                    <div className="text-[10px] uppercase tracking-wider text-white/40">{m.role}</div>
                    <div className="mt-1 whitespace-pre-wrap">{m.content}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
